/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Form, Input, Row, Select, Skeleton, Typography } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import { ArrowRightIcon } from '../../../Icons/ArrowRightIcon';

// Misc imports
import extractMsTeamsDataFromUrl from '../../../../utils/misc/extractMsTeamsDataFromUrl';
import is from '../../../../utils/is';
import replaceUrlParams from '../../../../utils/string/replaceUrlParams';
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG } from '../../../../constants/main';
import { NAV_ORGANIZATION_USERS } from '../../../../constants/navigation';
import { NODE_TYPES } from '../../../../constants/flowGraph';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { useFetchUserGroupsQuery } from '../../../../store/userGroups/userGroupsApi';

// Constant declarations
const { Text } = Typography;
const { Option } = Select;

const LiveChatNodeForm = ({ form, selectedElement }) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const { isEditingPrimaryLanguage } = useBotLanguage();

  const { currentUser } = useSelector((state) => state.auth);
  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: userGroups, isFetching: isFetchingUserGroups } = useFetchUserGroupsQuery({ orgId, urlParams: { pageSize: 100 } }, { skip: selectedElement.type !== NODE_TYPES.LIVECHAT });

  const userGroupsWithLiveAgentRole = userGroups && is.array(userGroups.items) ? userGroups.items.filter(g => g.organizationRoles.includes(ADDITIONAL_ROLES_ORG.LIVE_AGENT)) : [];
  const isOrgAdmin = currentUser?.effectiveRoles.organizationRoles?.[orgId]?.includes(PERMISSIONS_ORG.ADMIN);

  const [isGroupDeleted, setIsGroupDeleted] = useState(false);

  useEffect(() => {
    if (selectedElement?.type === NODE_TYPES.LIVECHAT || selectedElement?.type === NODE_TYPES.LIVECHAT_REQUEST) {
      if (selectedElement.data.livechatGroup && userGroupsWithLiveAgentRole.length > 0) {
        const groupExists = userGroupsWithLiveAgentRole.find(userGroup => userGroup.id === selectedElement.data.livechatGroup);
        !groupExists ? setIsGroupDeleted(true) : setIsGroupDeleted(false);
      } else {
        setIsGroupDeleted(false);
      }
    }
  }, [selectedElement, userGroupsWithLiveAgentRole]);

  if (bot.settings?.liveChat?.version === '1') {
    return (
      <Fragment>
        <Form.Item
          label={
            <Row>
              <Col span={24}>
                <Text>
                  {t('Channel ID or URL')}
                </Text>
              </Col>
              <Col span={24} className="mb-2">
                <Text type="secondary">
                  {t('You can get the channel URL by selecting')} <EllipsisOutlined /> <ArrowRightIcon className="fs-sm" /> {t('Get link to channel')}
                </Text>
              </Col>
            </Row>
          }
          name={['data', 'livechatChannelId']}
          rules={[
            {
              validator: async (_rule, value) => {
                return extractMsTeamsDataFromUrl(value).then(result => {
                  form.setFieldsValue({
                    data: {
                      livechatChannelId: result.channelId || form.getFieldValue(['data', 'livechatChannelId']),
                      azureTenantId: result.azureTenantId || form.getFieldValue(['data', 'azureTenantId'])
                    }
                  });

                  return Promise.resolve();
                }).catch((err) => {
                  return Promise.reject(new Error(err.channelId || t('Please make sure you entered everything correctly.')));
                });
              }
            }
          ]}
        >
          <Input
            size="large"
            disabled={!isEditingPrimaryLanguage}
          />
        </Form.Item>

        <Form.Item
          label={
            <Row>
              <Col span={24}>
                <Text>
                  {t('Azure tenant ID')}
                </Text>
              </Col>
              <Col span={24} className="mb-2">
                <Text type="secondary">
                  {t('You can paste the channel URL or type the ID individually')}
                </Text>
              </Col>
            </Row>
          }
          name={['data', 'azureTenantId']}
          rules={[
            {
              validator: async (_rule, value) => {
                if (value) {
                  return extractMsTeamsDataFromUrl(value).then(result => {
                    form.setFieldsValue({
                      data: {
                        livechatChannelId: result.channelId || form.getFieldValue(['data', 'livechatChannelId']),
                        azureTenantId: result.azureTenantId || form.getFieldValue(['data', 'azureTenantId'])
                      }
                    });

                    return Promise.resolve();
                  }).catch((err) => {
                    return Promise.reject(new Error(err.azureTenantId || t('Please make sure you entered everything correctly.')));
                  });
                }
              }
            }
          ]}
        >
          <Input
            size="large"
            disabled={!isEditingPrimaryLanguage}
          />
        </Form.Item>
      </Fragment>
    );
  } else {
    return (
      <Fragment>
        <Form.Item
          label={
            <Row>
              <Col span={24} className="mb-1">
                <Text>
                  {t('Assign to group')}
                </Text>
              </Col>
              <Col span={24} className="mb-2">
                <Text type="secondary">
                  {t('You can optionally select a User Group with the Live Agent role enabled, to assign LiveChat requests to. Only members of the selected group will be able to accept the requests.')}
                </Text>
              </Col>
            </Row>
          }
          name={['data', 'livechatGroup']}
        >
          {
            isFetchingUserGroups && is.emptyArray(userGroupsWithLiveAgentRole) ? (
              <Skeleton.Input active size="large" className="w-100 opacity-70 border-radius-base" />
            ) : (
              <Select
                status={isGroupDeleted ? 'error' : ''}
                placeholder={t('Select a group')}
                size="large"
                showSearch
                allowClear
                disabled={(isFetchingUserGroups && !userGroupsWithLiveAgentRole) || !isEditingPrimaryLanguage}
                loading={isFetchingUserGroups}
                filterOption={(input, option) => userGroupsWithLiveAgentRole.find(userGroup => userGroup.id === option.value)?.name.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                onChange={() => setIsGroupDeleted(false)}
                getPopupContainer={triggerNode => triggerNode.parentElement}
              >
                {
                  userGroupsWithLiveAgentRole
                    .slice() // Avoid mutating original array
                    .sort((a, b) => a.name && b.name && a.name.localeCompare(b.name))
                    .map(userGroup => (
                      <Option key={userGroup.id} value={userGroup.id}>{userGroup.name}</Option>
                    ))
                }
              </Select>
            )
          }
        </Form.Item>

        {
          isGroupDeleted ? (
            <Row>
              <Col span={24} className="my-3">
                <Text className="text-error fs-lg">
                  {t('The selected User Group with ID')} {selectedElement.data.livechatGroup} {t('has been deleted and requests are sent to all Live Agents of your organization.')}
                  {
                    !is.emptyArray(userGroupsWithLiveAgentRole) ? (
                      <Text className="text-error ml-1">
                        {t('Select another group to direct LiveChat requests to specific users.')}
                      </Text>
                    ) : null
                  }
                </Text>
              </Col>
            </Row>
          ) : null
        }

        {
          is.emptyArray(userGroupsWithLiveAgentRole) ? (
            <Row>
              <Col span={24} className="my-3">
                {t('There are no LiveChat groups available in your organisation.')}

                {
                  isOrgAdmin ? (
                    <Text>
                      {t('Click')}
                      <a
                        href={(`${replaceUrlParams(NAV_ORGANIZATION_USERS, { orgId })}?tab=user-groups`)}
                        className="mx-1"
                      >
                        {t('here')}
                      </a>
                      {t('to create one')}
                    </Text>
                  ) : null
                }
              </Col>
            </Row>
          ) : null
        }
      </Fragment>
    );
  }
};

LiveChatNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object.isRequired
};

export default LiveChatNodeForm;
